import { PORT_CONTAINER_CLASS, PORT_SELECTOR_CLASS, WIDGETBASE_CLASS } from 'constants/bounding-box';
import { PROXIMITY_RADIUS } from 'constants/editor';
import { WrapperPos } from 'types/flowCore.types';
import { GroupTargetData } from 'types/flowCore.types';
import { WidgetId } from 'types/idTypes';
import { WidgetType } from 'types/widget.types';
import { getBoundingRectOfRotatedWidget, getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { GroupWidgetData } from 'widgets/Widget.types';
import { groupIdCache } from 'widgets/sdk/GroupIdCache';

import { parseStrictNumber } from './number';

const detectElement = (clientX: number, clientY: number, className: string): HTMLElement | null =>
  document
    .elementsFromPoint(clientX, clientY)
    .find((element) =>
      element?.classList?.contains(className) ? element : element?.closest(`.${className}`),
    ) as HTMLElement;
/**
 * Check that the mouse cursor detects the port element.
 * @param clientX
 * @param clientY
 * @returns
 */
export const detectPortElement = (clientX: number, clientY: number): HTMLElement | null =>
  detectElement(clientX, clientY, PORT_SELECTOR_CLASS);

/**
 * Check that the mouse cursor detects the port container.
 * @param clientX
 * @param clientY
 * @returns
 */
export const detectPortContainer = (clientX: number, clientY: number): HTMLElement | null =>
  detectElement(clientX, clientY, PORT_CONTAINER_CLASS);

/**
 * Detects a widget element in the proximity of the mouse cursor.
 *
 * This function searches for a widget element at the cursor's exact position and
 * in a circular area around the cursor. It uses a predefined radius to define
 * the proximity area and checks at equally spaced points around the cursor.
 *
 * @param {number} clientX - The X-coordinate of the mouse cursor.
 * @param {number} clientY - The Y-coordinate of the mouse cursor.
 * @returns {HTMLElement | undefined} The detected widget element, or undefined if none is found.
 */
export const detectWidgetElementWithProximity = (clientX: number, clientY: number): HTMLElement | null => {
  // We want to check 6 points around the cursor, so we divide the circle into 6 segments
  // A full circle in radians is 2π, so each segment will be 2π / 6 radians apart
  const ANGLE_STEP = Math.PI / 3; // This is the angular distance between each point

  // First, try to detect a widget at the exact position of the cursor
  let detectedElement = detectWidgetElement(clientX, clientY);
  if (detectedElement) {
    return detectedElement;
  }

  // If no widget is found at the cursor, check around it in a circle
  for (let i = 0; i < 6; i++) {
    // Calculate the angle for the current point
    const angle = ANGLE_STEP * i;

    // Calculate the x and y coordinates for each point around the cursor
    // Math.cos(angle) gives the horizontal distance from the cursor to the point on the circle
    // Math.sin(angle) gives the vertical distance from the cursor to the point on the circle
    const x = clientX + PROXIMITY_RADIUS * Math.cos(angle); // New x-coordinate
    const y = clientY + PROXIMITY_RADIUS * Math.sin(angle); // New y-coordinate

    // Try to detect a widget at this new point
    detectedElement = detectWidgetElement(x, y);
    if (detectedElement) {
      // If a widget is found at this point, return it
      return detectedElement;
    }
  }

  // If no widgets are found in the vicinity, return null
  return null;
};

/**
 * Check that the mouse cursor detects widget elements except for line widgets
 * @param clientX
 * @param clientY
 * @returns
 */
export const detectWidgetElement = (clientX: number, clientY: number): HTMLElement | null => {
  const targetWidgetElement =
    (document.elementsFromPoint(clientX, clientY).find((element) => {
      const widgetContainer = element?.classList?.contains(WIDGETBASE_CLASS)
        ? element
        : element?.closest(`.${WIDGETBASE_CLASS}`);
      if (!widgetContainer) return false;
      return getWidgetTypeFromId(widgetContainer.id) !== WidgetType.Line; // Ignore line widget
    }) as HTMLElement) ?? null;
  const widgetContainer =
    (targetWidgetElement?.classList?.contains(WIDGETBASE_CLASS)
      ? targetWidgetElement
      : (targetWidgetElement?.closest(`.${WIDGETBASE_CLASS}`) as HTMLElement)) ?? null;

  return widgetContainer;
};

/**
 * Detects the group widget element that is nearest to the cursor position.
 *
 * @param clientX - The X-coordinate of the cursor position.
 * @param clientY - The Y-coordinate of the cursor position.
 * @param allGroupWidgets - An array containing information about all group widgets.
 * @param [withProximityCheck = false] - flag to check if widget has proximity to the cursor position
 *
 * @returns A GroupTargetData object containing information about the detected group widget, or undefined if not found.
 */
export const detectNearestGroupElement = (
  clientX: number,
  clientY: number,
  allGroupWidgets: { widgetId: string; widgetData: GroupWidgetData }[],
  withProximityCheck: boolean = false,
): GroupTargetData | null => {
  // Step 1: Use detectWidgetElement to find the nearest widget element.
  let detectedWidgetElement = null;
  if (withProximityCheck) {
    detectedWidgetElement = detectWidgetElementWithProximity(clientX, clientY);
  } else {
    detectedWidgetElement = detectWidgetElement(clientX, clientY);
  }

  // Step 2: Extract id from found widget element.
  if (!detectedWidgetElement) return null;
  const detectedWidgetId = detectedWidgetElement.id;

  const universalGroupId = groupIdCache.getParentIdNested(detectedWidgetId);

  // Step 3: Find the associated group using the widget's id.
  return universalGroupId ? getGroupTargetData(universalGroupId, allGroupWidgets) : null;
};

/**
 * Retrieves the data of a target group associated with a given widget ID.
 *
 * @param {WidgetId} widgetId - The ID of the widget.
 * @param {GroupWidgetData} widgetData - The data of the group of widgets.
 * @returns {GroupTargetData | undefined} - The target data of the group associated with the given widget ID,
 * or undefined if the group does not exist.
 *
 * @example
 * const targetData = getGroupTargetDataFromWidgetId(someWidgetId, someWidgetData);
 */
export const getGroupTargetDataFromWidgetId = (
  widgetId: WidgetId,
  widgetData: GroupWidgetData,
): GroupTargetData | undefined => {
  const universalGroupId = groupIdCache.getParentIdNested(widgetId);

  // If the group exists, return the group's data
  if (universalGroupId) {
    const groupWidgetPosition = getGroupWidgetPositionFromWidgetIds(widgetData.memberWidgetIds);
    return {
      groupId: universalGroupId,
      wrapperPos: groupWidgetPosition,
    };
  }
  return undefined;
};

export const getGroupTargetData = (
  groupId: string,
  allGroupWidgets: { widgetId: string; widgetData: GroupWidgetData }[],
): GroupTargetData | null => {
  const associatedGroup = allGroupWidgets.find(({ widgetId }) => widgetId === groupId);

  // If the group exists, return the group's data
  if (associatedGroup) {
    const groupWidgetPosition = getGroupWidgetPositionFromWidgetIds(associatedGroup.widgetData.memberWidgetIds);
    return {
      groupId: associatedGroup.widgetId,
      wrapperPos: groupWidgetPosition,
    };
  }

  // If no group found
  return null;
};

/**
 * Return the parent widget element
 * @param widgetElm
 * @returns {HTMLElement | null}
 */
export const getParentWidgetElement = (widgetElm: HTMLElement): HTMLElement | null => {
  const parentElement = widgetElm.parentElement?.closest(`.${WIDGETBASE_CLASS}`) as HTMLElement;
  return parentElement || null;
};

/**
 * Calculate the matrix value using the element's transform value
 * @param targe {HTMLElement}
 * @returns {DOMMatrix} https://developer.mozilla.org/en-US/docs/Web/API/DOMMatrix/DOMMatrix
 */
export const getDOMMatrix = (target: HTMLElement): DOMMatrix => {
  return new DOMMatrix(getComputedStyle(target).transform);
};

/**
 * return the WrapperPosition by html element
 * @param targetElm
 * @returns {WrapperPos}
 */
export const getWidgetPosition = (targetElm: HTMLElement): WrapperPos => {
  const result = {
    topPx: parseStrictNumber(targetElm.offsetTop),
    leftPx: parseStrictNumber(targetElm.offsetLeft),
    widthPx: parseStrictNumber(targetElm.clientWidth),
    heightPx: parseStrictNumber(targetElm.clientHeight),
  };

  return result;
};

/**
 * Returns the rotation of the targetElm
 * @param targetElm
 * @returns {Number} angle in degrees of the targetted element
 */
export const getWidgetRotation = (targetElm: HTMLElement): number => {
  const st = window.getComputedStyle(targetElm, null);
  const tr = st?.getPropertyValue('transform');

  let angle = 0;
  if (tr && tr !== 'none') {
    const values = tr.split('(')[1].split(')')[0].split(',');
    const a = parseFloat(values[0]);
    const b = parseFloat(values[1]);
    angle = Math.round(Math.atan2(b, a) * (180 / Math.PI));
  }

  return angle;
};

/**
 * Return the calculated offset of all parent widgets of the target element (Responsive widget and Group widget)
 * @param targetElm
 * @param offset
 *
 * @returns
 */
export const calculateAllParentWidgetOffset = (
  targetElm: HTMLElement,
  offset: { leftPx: number; topPx: number } = { leftPx: 0, topPx: 0 },
) => {
  let result = offset;
  const parentElement = getParentWidgetElement(targetElm);
  if (parentElement) {
    const parentWrapperPosition = getWidgetPosition(parentElement);
    result.leftPx += parentWrapperPosition.leftPx;
    result.topPx += parentWrapperPosition.topPx;
    result = calculateAllParentWidgetOffset(parentElement, offset);
  }

  return result;
};

/**
 * Get the transform translateX, translateY from an element
 */
export const getTranslateXY = (element: HTMLElement) => {
  const style = (window as any).getComputedStyle(element);
  const matrix = new DOMMatrixReadOnly(style.transform);

  return {
    translateX: matrix.m41,
    translateY: matrix.m42,
  };
};

/**
 * Return the rotate deg value from the style.transform
 *
 * @param style
 * @returns
 */
export const getRotatDegFromStyle = (style: CSSStyleDeclaration | undefined): number => {
  if (!style) return 0;

  const rotateData = style.transform.match(/rotate\((.+)\)/);
  return (rotateData && parseStrictNumber(rotateData?.[1])) || 0;
};

/**
 * Retrieves the HTML element associated with a given widget ID.
 *
 * @param widgetId - The ID of the widget.
 *
 * @returns The HTML element corresponding to the widget ID, or null if not found.
 */
export const getElementForWidgetId = (widgetId: string): HTMLElement | null => {
  return document.getElementById(widgetId);
};

/**
 * Retrieves the HTML elements corresponding to a list of widget IDs.
 *
 * @param widgetIds - An array of widget IDs.
 *
 * @returns An array of HTML elements corresponding to the given widget IDs.
 */
export const getElementsForGroup = (widgetIds: string[]): HTMLElement[] => {
  return widgetIds.map((widgetId) => getElementForWidgetId(widgetId)).filter(Boolean) as HTMLElement[];
};

/**
 * Calculates the wrapper position (top, left, width, height) for a group of widgets based on their IDs.
 *
 * @param widgetIds - An array of widget IDs that make up the group.
 *
 * @returns A WrapperPos object containing the top, left, width, and height of the group.
 */
export const getGroupWidgetPositionFromWidgetIds = (widgetIds: string[]): WrapperPos => {
  const elements = getElementsForGroup(widgetIds);

  if (elements.length === 0) {
    // Return default wrapper position if no elements found
    return { topPx: 0, leftPx: 0, widthPx: 0, heightPx: 0 };
  }

  // Initialize boundaries
  let topBoundary = Number.POSITIVE_INFINITY;
  let leftBoundary = Number.POSITIVE_INFINITY;
  let rightBoundary = Number.NEGATIVE_INFINITY;
  let bottomBoundary = Number.NEGATIVE_INFINITY;

  elements.forEach((element) => {
    const position = getWidgetPosition(element);
    const rotation = getWidgetRotation(element);

    const boundingRect = getBoundingRectOfRotatedWidget(
      position.leftPx,
      position.topPx,
      position.widthPx,
      position.heightPx,
      rotation,
    );

    // Update boundaries
    topBoundary = Math.min(topBoundary, boundingRect.topPx);
    leftBoundary = Math.min(leftBoundary, boundingRect.leftPx);
    rightBoundary = Math.max(rightBoundary, boundingRect.leftPx + boundingRect.widthPx);
    bottomBoundary = Math.max(bottomBoundary, boundingRect.topPx + boundingRect.heightPx);
  });

  return {
    topPx: topBoundary,
    leftPx: leftBoundary,
    widthPx: rightBoundary - leftBoundary,
    heightPx: bottomBoundary - topBoundary,
  };
};

// Check if a style (target.style.width, target.style.height) is a px value or string (auto, fit-content, percentage, etc)
export const isTargetStyleNumber = (style: string) => !Number.isNaN(parseFloat(style)) && style.indexOf('%') === -1;

/**
 * Sets the position and dimensions for a specified widget's element.
 *
 * @param {WidgetId} widgetId - The unique identifier for the widget element to be targeted.
 * @param {WrapperPos} wrapperPos - An object containing position (left and top) and dimensions (height and width) in pixels.
 *
 * @example
 * setWidgetElementWrapperPosition("widget123", { leftPx: 10, topPx: 20, heightPx: 300, widthPx: 200 });
 */
export const setWidgetElementWrapperPosition = (
  widgetId: WidgetId,
  { leftPx, topPx, heightPx, widthPx }: WrapperPos,
) => {
  let originalTarget = document.querySelector(`[id="${widgetId}"]:not(.moveable-area)`);
  if (originalTarget) {
    let originalTargetElement = originalTarget as HTMLElement;
    originalTargetElement.style.left = `${leftPx}px`;
    originalTargetElement.style.top = `${topPx}px`;
    originalTargetElement.style.height = `${heightPx}px`;
    originalTargetElement.style.width = `${widthPx}px`;
  }
};

/**
 * Calculates the scaled dimensions of a child element to fit within the parent container
 *
 * @param {number} containerElWidth - width of the container element.
 * @param {number} containerElHeight - height of the container element.
 * @param {number} widthPx - pixel width of the child element.
 * @param {number} heightPx - pixel height of the child element.
 * @param {number} [paddingOffset = 0] - optional padding to subtract from the container's dimensions.
 * @returns an object that contains the dimension (width, height) and scale values for the child element.
 */
export const fitToContainer = (
  containerElWidth: number,
  containerElHeight: number,
  widthPx: number,
  heightPx: number,
  paddingOffset: number = 0,
) => {
  // Calculate container size with padding offsets
  const containerWidth = containerElWidth - paddingOffset;
  const containerHeight = containerElHeight - paddingOffset;

  // Get the scale of the target element size relative to container size
  const widthScale = containerWidth / widthPx;
  const heightScale = containerHeight / heightPx;

  // Returns minimum scale value to apply the smallest scale value that can fit both horizontal and vertical layout
  const scale = Math.min(widthScale, heightScale) || 1;

  // Get the values of width and height derived from scale
  const width = widthPx * scale;
  const height = heightPx * scale;

  return { width, height, factorScale: scale, widthScale, heightScale };
};
